import React from 'react';
import { FiArrowLeft, FiAlertCircle, FiArrowUp, FiCheckCircle, FiBarChart2, FiFileText, FiClock } from 'react-icons/fi';
import IconWrapper from '../IconWrapper';

type InsightType = 'critical' | 'emerging' | 'positive';
type FrameworkType = 'TCF' | 'NCD';
type SeverityLevel = 'High' | 'Medium' | 'Low';

interface RecommendedAction {
  action: string;
  expectedImpact: string;
}

interface InsightDetailProps {
  id: string;
  type: InsightType;
  title: string;
  framework: FrameworkType;
  frameworkDetail: string;
  frameworkDescription: string;
  severity: SeverityLevel;
  confidence: number;
  timePeriod: string;
  analysisPoints: string[];
  rootCauseAssessment: string;
  recommendedActions: RecommendedAction[];
  onBack: () => void;
}

const InsightDetail: React.FC<InsightDetailProps> = ({
  id,
  type,
  title,
  framework,
  frameworkDetail,
  frameworkDescription,
  severity,
  confidence,
  timePeriod,
  analysisPoints,
  rootCauseAssessment,
  recommendedActions,
  onBack
}) => {
  const getIcon = () => {
    switch (type) {
      case 'critical':
        return <IconWrapper icon={FiAlertCircle} className="text-amber-500" size={24} />;
      case 'emerging':
        return <IconWrapper icon={FiArrowUp} className="text-blue-500" size={24} />;
      case 'positive':
        return <IconWrapper icon={FiCheckCircle} className="text-green-500" size={24} />;
      default:
        return null;
    }
  };

  const getSeverityColor = () => {
    switch (severity) {
      case 'High':
        return 'text-red-600';
      case 'Medium':
        return 'text-amber-600';
      case 'Low':
        return 'text-green-600';
      default:
        return '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm">
      <div className="border-b border-gray-200 p-5 flex justify-between items-center">
        <h2 className="text-lg font-bold">INSIGHT DETAILS</h2>
        <button
          onClick={onBack}
          className="flex items-center text-primary hover:underline"
        >
          <IconWrapper icon={FiArrowLeft} className="mr-1" />
          Back to List
        </button>
      </div>

      <div className="p-5">
        <div className="flex items-center mb-4">
          {getIcon()}
          <span className="ml-2 text-xl font-medium">{title}</span>
        </div>

        <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <div className="text-sm font-semibold text-primary mb-1">
              {framework} Classification: {frameworkDetail}
            </div>
            <div className="text-sm">{frameworkDescription}</div>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-secondary/70 mb-1">Severity</div>
              <div className={`text-sm font-semibold ${getSeverityColor()}`}>{severity}</div>
            </div>
            <div>
              <div className="text-xs text-secondary/70 mb-1">Confidence</div>
              <div className="text-sm font-semibold">{confidence}%</div>
            </div>
            <div>
              <div className="text-xs text-secondary/70 mb-1">Time Period</div>
              <div className="text-sm font-semibold">{timePeriod}</div>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-bold mb-3 flex items-center">
            <IconWrapper icon={FiBarChart2} className="mr-2 text-primary" size={20} />
            ANALYSIS
          </h3>
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <ul className="pl-5 space-y-2">
              {analysisPoints.map((point, index) => (
                <li key={index} className="text-sm list-disc">{point}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-bold mb-3 flex items-center">
            <IconWrapper icon={FiFileText} className="mr-2 text-primary" size={20} />
            ROOT CAUSE ASSESSMENT
          </h3>
          <div className="text-sm bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
            {rootCauseAssessment}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-bold mb-3 flex items-center">
            <IconWrapper icon={FiClock} className="mr-2 text-primary" size={20} />
            RECOMMENDED ACTIONS
          </h3>
          <div className="space-y-4">
            {recommendedActions.map((action, index) => (
              <div key={index} className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm transition-all hover:shadow-md">
                <div className="flex items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-secondary font-bold">
                    {index + 1}
                  </div>
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-semibold mb-2">
                      {action.action}
                    </div>
                    <div className="flex items-center">
                      <div className="text-xs font-medium bg-green-100 text-green-800 px-2 py-1 rounded">
                        Expected impact: {action.expectedImpact}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <button className="text-primary hover:text-primary-dark px-2 py-1 text-sm font-medium rounded border border-primary hover:bg-primary/5 transition-colors">
                      Assign
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-6">
          <h3 className="text-base font-bold mb-3 flex items-center">
            <IconWrapper icon={FiBarChart2} className="mr-2 text-primary" size={20} />
            SUPPORTING EVIDENCE
          </h3>
          <div className="flex flex-wrap gap-2">
            <button className="px-3 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center">
              <IconWrapper icon={FiFileText} className="mr-2" size={16} />
              View 12 Example Complaints
            </button>
            <button className="px-3 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center">
              <IconWrapper icon={FiBarChart2} className="mr-2" size={16} />
              View Trend Analysis
            </button>
            <button className="px-3 py-2 text-sm text-primary border border-primary rounded-lg hover:bg-primary/5 transition-colors flex items-center">
              <IconWrapper icon={FiClock} className="mr-2" size={16} />
              View Similar Historical Cases
            </button>
          </div>
        </div>

        <div>
          <h3 className="text-base font-bold mb-3 flex items-center">
            <IconWrapper icon={FiCheckCircle} className="mr-2 text-primary" size={20} />
            ACTION TRACKING
          </h3>
          <div className="bg-white border border-gray-200 p-4 rounded-lg shadow-sm">
            <div className="flex flex-wrap gap-3">
              <label className="flex items-center bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg cursor-pointer transition-colors">
                <input type="checkbox" className="form-checkbox h-5 w-5 text-primary rounded" />
                <span className="ml-2 text-sm font-medium">Mark as Acknowledged</span>
              </label>
              <label className="flex items-center bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg cursor-pointer transition-colors">
                <input type="checkbox" className="form-checkbox h-5 w-5 text-primary rounded" />
                <span className="ml-2 text-sm font-medium">Assign to Team</span>
              </label>
              <label className="flex items-center bg-gray-50 hover:bg-gray-100 px-3 py-2 rounded-lg cursor-pointer transition-colors">
                <input type="checkbox" className="form-checkbox h-5 w-5 text-primary rounded" />
                <span className="ml-2 text-sm font-medium">Create Action Plan</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InsightDetail;